import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatCurrency } from '../lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { BarChart3 } from 'lucide-react';

export default function ReportsPage() {
  const [dashboard, setDashboard] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard'),
      api.get('/invoices'),
    ]).then(([d, i]) => {
      setDashboard(d.data);
      setInvoices(i.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-slate-500">Loading reports...</div>;
  if (!dashboard) return null;

  // Invoice status distribution
  const statusCounts = {};
  invoices.forEach(inv => {
    statusCounts[inv.status] = (statusCounts[inv.status] || 0) + 1;
  });
  const statusData = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));
  const COLORS = ['#64748B', '#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#94A3B8'];

  // Summary data
  const summaryData = [
    { name: 'Receivable', value: parseFloat(dashboard.total_receivable) || 0 },
    { name: 'Collected', value: parseFloat(dashboard.total_collected) || 0 },
    { name: 'Expenses', value: parseFloat(dashboard.total_expenses) || 0 },
  ];

  const netIncome = (parseFloat(dashboard.total_collected) || 0) - (parseFloat(dashboard.total_expenses) || 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="reports-title">Reports</h1>
        <p className="text-sm text-slate-500 mt-1">Financial overview and analytics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">Total Receivable</p>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(dashboard.total_receivable)}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">Total Collected</p>
            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(dashboard.total_collected)}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500">Net Income</p>
            <p className={`text-2xl font-bold ${netIncome >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{formatCurrency(netIncome)}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-medium">Financial Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={summaryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="value" fill="#2563EB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-medium">Invoice Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {statusData.length === 0 ? (
              <div className="flex items-center justify-center h-[300px] text-slate-500">No invoice data</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {statusData.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
